import React, { useState, useCallback, useEffect, useRef } from 'react';
import Cropper from 'react-easy-crop';
import ReactSlider from 'react-slider';
import { Modal, Button } from 'react-bootstrap';
import { getCroppedImg } from './getCroppedImg';
import { Link, useNavigate } from "react-router-dom";
import OrgSettingModal from "../../../../components/modelpopup/OrgSettingModal";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import Swal from 'sweetalert2'; // Import SweetAlert


const ProfileSetting = () => {
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [hoveredOrg, setHoveredOrg] = useState(null);
  const [showModal, setShowModal] = useState(true);
  const [isFullScreen, setFullScreen] = useState(false);
  const maximizeBtnRef = useRef(null);
  const [profileImage, setProfileImage] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');
  const cdnUrl = process.env.REACT_APP_CDN_URL;
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);



  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };



    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener('click', handleClick);

    return () => {
      maximizeBtn.removeEventListener('click', handleClick);
      cleanup();
    };
  }, [isFullScreen]);



  // Inside the ProfileSetting component:
  
  const handleDeleteClick = (orgId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'All data related to this organization will be deleted!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // API call to delete the organization
          await axios.delete(`${apiUrl}/company/delete/${orgId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
  
          // Remove the organization from the state
          setOrganizations((prevOrgs) =>
            prevOrgs.filter((org) => org.id !== orgId)
          );
  
          Swal.fire('Deleted!', 'The organization has been deleted.', 'success');
        } catch (error) {
          console.error('Error deleting organization:', error);
          Swal.fire('Error!', 'Failed to delete the organization.', 'error');
        }
      }
    });
  };
  

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${apiUrl}/company/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        console.log("API Response:", response.data); // Debugging response
  
        // Ensure the `organizations` key exists and is an array
        if (response.data && Array.isArray(response.data.organizations)) {
          const organizationsData = response.data.organizations;
  
          // Map response to match the expected format
          const mappedOrganizations = organizationsData.map((org) => ({
            id: org.company_id, // Use company_id as id
            logo: org.company_logo
              ? `${cdnUrl}/${org.company_logo}` // Full logo URL
              : "https://via.placeholder.com/100", // Fallback logo
            name: org.company_name || "Unnamed Organization", // Fallback for missing name
            phone: org.company_phone || "N/A", // Fallback for missing phone
            email: org.company_email || "N/A", // Fallback for missing email
            company_address : org.company_address || "", // Fallback for missing email
            company_unique_id : org.company_unique_id || "", // Fallback for missing email
            contact_person	 : org.contact_person	 || "", // Fallback for missing email
            contact_designation	: org.contact_designation	 || "",
            company_date_format : org.company_date_format	 || "",
            company_currency	: org.company_currency	 || "",
            type:org.type,
          }));
  
          console.log("Mapped Organizations:", mappedOrganizations); // Debugging mapped data
          setOrganizations(mappedOrganizations);
          setLoading(false);

        } else {
          toast.warn("No organizations found.");
          setLoading(false);

        }
      } catch (error) {
        console.error("Error fetching organizations:", error.message); // Log errors
        toast.error("Unable to fetch organizations. Please try again.");
        setLoading(false);

      }
    };
  
    fetchOrganizations();
  }, [apiUrl, cdnUrl, token]);
  
  

  const handleImageChange = (e, org) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setSelectedOrg(org); // Set the selected organization for cropping
        setIsCropping(true); // Trigger the modal
      };
      reader.readAsDataURL(file);
    }
  };

 // Define the handleModalClose function
 const handleModalClose = () => {
  setShowModal(false); // Close the modal
};


const handleAddOrganization = () => {
  // Reset the modal fields or set default values for Add mode
  setShowModal(true);
  setSelectedOrg(null); // Reset `orgData` to null or undefined for Add mode
};


  const handleEditClick = (org) => {
    setSelectedOrg(org);

    setShowModal(true);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);


  const handleCropSave = async () => {
    if (!selectedOrg || !selectedOrg.id) {
      toast.error("No organization selected for updating the logo.");
      return;
    }
  
    try {
      const croppedImage = await getCroppedImg(previewImage, croppedAreaPixels);
  
      // Update the organization's logo in the state
      setOrganizations((prev) =>
        prev.map((org) =>
          org.id === selectedOrg.id ? { ...org, logo: croppedImage } : org
        )
      );
  
      const formData = new FormData();
      formData.append('image', croppedImage);
      formData.append('orgid', selectedOrg.id); // Use `selectedOrg.id` to pass the organization ID
  
      const response = await axios.post(`${apiUrl}/company/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (response.status === 200) {
        // Update profile image in the organization list
        const { logoUrl  } = response.data;

        // Update the selected organization with the new logo
        setOrganizations((prev) =>
          prev.map((org) =>
            org.id === selectedOrg.id ? { ...org, logo: logoUrl	 } : org
          )
        );
        setProfileImage(logoUrl);

        toast.success("Logo updated successfully!");
      } else {
        toast.error("Failed to update logo.");
      }
      setIsCropping(false); // Hide cropper modal
    } catch (error) {
      console.error("Error saving cropped image:", error);
      toast.error("An error occurred while updating the logo.");
    }
  };
  


  const handleCropSave1 = async () => {
    try {
      const croppedImage = await getCroppedImg(previewImage, croppedAreaPixels);

      // Update the organization's logo in the state
      setOrganizations((prev) =>
        prev.map((org) =>
          org.id === selectedOrg.id ? { ...org, logo: croppedImage } : org
        )
      );

      setIsCropping(false);
      setPreviewImage(null);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  const handleCancel = () => {
    setIsCropping(false);
    setPreviewImage(null);
  };

  const handleModalSubmit = (updatedOrg) => {
    // Extract the company data from the updatedOrg object
    const company = updatedOrg.company;
  
    console.log("Updated Organization:", company);
  
    setOrganizations((prevOrgs) => {
      const orgIndex = prevOrgs.findIndex((org) => org.id === company.companyid);
  
      if (orgIndex > -1) {
        // Update the existing organization
        return prevOrgs.map((org, index) =>
          index === orgIndex ? { ...org, ...company, id: company.companyid } : org
        );
      } else {
        // Add new organization
        return [
          ...prevOrgs,
          {
            ...company,
            id: company.companyid, // Ensure the ID is properly mapped
          },
        ];
      }
    });
  
  };
  
  

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">

      <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-4">
              <h3 className="page-title">Manage Organization</h3>
            </div>
            <div className="col-md-8 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <Link to="#" className="grid-view btn btn-link">
                    <i className="las la-redo-alt" />
                  </Link>
                  <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                    <i className="las la-expand-arrows-alt" />
                  </Link>
                </div>
       
                <Link
                to="#"
                className="btn add-btn custom-btn"
                onClick={() => handleAddOrganization()}
                data-bs-toggle="modal"
                data-bs-target="#organizationSettingsModal"
              >
                <i className="la la-plus-circle custom-icon" /> Add Organization
              </Link>

              </div>
            </div>
          </div>
          </div>


          <div className="row">
  {loading ? (
    <div id="loader-wrapper" style={{ textAlign: "center", margin: "20px 0" }}>
      <div className="loader">
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
        <div className="dot"></div>
      </div>
    </div>
  ) : (
    organizations.map((org) => (
      <div
        key={org.id}
        className="col-md-4 col-sm-6 col-12"
        style={{
          borderRadius: "5px",
          marginBottom: "20px",
        }}
      >
        <div
          className="profile-widget"
          style={{
            borderRadius: "5px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            position: "relative",
            backgroundColor: org.type === "Current" ? "#f0f8ff" : "#fff", // Light blue for Current organization
          }}
        >
          {org.type === "Current" && (
            <div
              style={{
                position: "absolute",
                top: "80%",
                left: 0,
                transform: "translateY(-50%) rotate(-90deg)",
                transformOrigin: "left center",
                backgroundColor: "#007bff",
                color: "#fff",
                padding: "0px 15px",
                borderRadius: "5px 5px 0 0",
                fontSize: "14px",
                fontWeight: "bold",
                marginLeft: "10px",
                whiteSpace: "nowrap",
              }}
            >
              Current Organization
            </div>
          )}

          {/* Profile Image */}
     
          <div
  className="profile-img"
  style={{
    width: "210px",
    height: "80px",
    position: "relative",
    overflow: "hidden",
  }}
  onMouseEnter={() => setHoveredOrg(org.id)}
  onMouseLeave={() => setHoveredOrg(null)}
>
  <img
    src={org.logo || profileImage}
    alt={`${org.name} Logo`}
    style={{
      width: "100%",
      height: "100%",
      objectFit: "cover",
    }}
  />
  {hoveredOrg === org.id && (
    <div
      className="profile-img-hover"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10,
        cursor: "pointer", // Ensure the hover cursor indicates clickability
      }}
      onClick={() => {
        const inputElement = document.getElementById(`image-${org.id}`);
        console.log("Input Element:", inputElement); // Debugging
        if (inputElement) {
          inputElement.click();
        } else {
          console.error(`Input element for org ${org.id} not found`);
        }
      }}
    >
      <i
        className="fa fa-camera"
        style={{
          fontSize: "24px",
          color: "white",
        }}
      />
    </div>
  )}
  <input
    type="file"
    id={`image-${org.id}`}
    className="d-none"
    onChange={(e) => handleImageChange(e, org)}
  />
</div>


          {/* Dropdown Action */}
          <div className="dropdown profile-action">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="material-icons">more_vert</i>
            </Link>
            <div className="dropdown-menu dropdown-menu-right">
              <Link
                className="dropdown-item"
                data-bs-toggle="modal"
                data-bs-target="#organizationSettingsModal"
                onClick={(e) => {
                  e.preventDefault();
                  handleEditClick(org);
                }}
              >
                <i className="fa fa-pencil m-r-5" /> Edit
              </Link>
              <button
                className="dropdown-item"
                onClick={() => handleDeleteClick(org.id)}
              >
                <i className="fa fa-trash m-r-5" /> Delete
              </button>

            </div>
          </div>

          {/* Organization Name */}
          <h4
            className="organization-name"
            style={{ marginBottom: "8px", fontWeight: "bold" }}
          >
            {org.name}
          </h4>
          <p className="organization-phone" style={{ marginBottom: "4px" }}>
            {org.phone}
          </p>
          <p className="organization-email" style={{ marginBottom: "0" }}>
            {org.email}
          </p>

          {/* Separator and Bottom Links */}
          <hr style={{ borderTop: "1px solid #ddd", margin: "6px 0" }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 5px 5px",
            }}
          >
            {org.type === "Current" ? (
              <>
                <Link
                  to={`/manage-locations`}
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <i
                    className="fa fa-map-marker"
                    style={{ marginRight: "5px" }}
                  />
                  Locations #10
                </Link>
                <Link
                  to={`/organization/${org.id}/users`}
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <i className="fa fa-users" style={{ marginRight: "5px" }} />
                  Users #10
                </Link>
              </>
            ) : (
              <>
                <div style={{ color: "#555", display: "flex" }}>
                  <i className="fa fa-map-marker" style={{ marginRight: "5px" }} />
                  Locations #10
                </div>
                <div style={{ color: "#555", display: "flex" }}>
                  <i className="fa fa-users" style={{ marginRight: "5px" }} />
                  Users #10
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    ))
  )}
</div>


      {/* Cropping Modal */}
      <Modal show={isCropping} onHide={handleCancel} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Update Organization Logo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ position: "relative", width: "100%", height: "400px" }}>
              <Cropper
                image={previewImage}
                crop={crop}
                zoom={zoom}
                aspect={4 / 1} // Aspect ratio for wide logos
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCropSave}>
              Update Logo
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
           {/* Organization Settings Modal */}
           {showModal && (

        <OrgSettingModal
        showModal={showModal}
        onSubmit={handleModalSubmit}
        orgData={selectedOrg}
      />
        
      )}
    </div>
   
   
  );
};

export default ProfileSetting;
